<template>
	<LayoutFlex
		background="--background-base"
		padding="var(--size-32) var(--size-20)"
		gap="var(--size-16)"
		align-items="center"
		justify-content="center"
		:style="{ minHeight: '100dvh' }"
	>
		<template v-if="auth.initError">
			<Text font="--text-14-20-reg" color="--text-dark-primary" text-align="center">
				Couldn’t sign you in: {{ auth.initError }}
			</Text>
			<Button @ds:click="manualRetry">
				<Text font="--text-16-18-semi">Try again</Text>
			</Button>
		</template>
		<div v-else class="signin__spinner" role="status" aria-label="Signing in" />
	</LayoutFlex>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Button, LayoutFlex, Text } from '@simple-life-app/growth-ui-kova'
import * as Sentry from '@sentry/vue'
import { useAuthStore } from '@/store/auth'
import { devBypassEnabled } from '@/auth/devBypass'
import { flushAnalytics } from '@/analytics/client'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

/** `?next` from the current URL OR the appState.next stashed by init() from the Auth0 callback. */
const requestedNext = computed<string>(() => {
	const fromQuery = Array.isArray(route.query.next) ? route.query.next[0] : route.query.next
	if (typeof fromQuery === 'string' && fromQuery.startsWith('/') && !fromQuery.startsWith('//')) {
		return fromQuery
	}
	return '/status'
})

/**
 * Loop guard. If we auto-redirect to Auth0 and somehow end up back on /signin without
 * a code+state and without an `?error=…`, the next mount would auto-redirect again,
 * potentially in an infinite loop. Bail after 3 auto-attempts in 60 seconds and show
 * a manual retry button instead.
 */
const SIGNIN_LOOP_KEY = 'med-account-signin-auto-attempts'
const MAX_AUTO_ATTEMPTS = 3
const LOOP_WINDOW_MS = 60_000

function recordAutoAttempt(): { allowed: boolean; count: number } {
	const now = Date.now()
	let attempts: number[] = []
	try {
		const raw = sessionStorage.getItem(SIGNIN_LOOP_KEY)
		if (raw) attempts = JSON.parse(raw) as number[]
	} catch {
		attempts = []
	}
	attempts = attempts.filter((t) => now - t < LOOP_WINDOW_MS)
	attempts.push(now)
	sessionStorage.setItem(SIGNIN_LOOP_KEY, JSON.stringify(attempts))
	return { allowed: attempts.length <= MAX_AUTO_ATTEMPTS, count: attempts.length }
}

function clearLoopGuard() {
	sessionStorage.removeItem(SIGNIN_LOOP_KEY)
}

/** Extracts `?error=…` / `?error_description=…` from the current URL (set by Auth0 on failure). */
function readUrlError(): string | null {
	const params = new URLSearchParams(window.location.search)
	const error = params.get('error')
	if (!error) return null
	const desc = params.get('error_description')
	return desc ? `${error}: ${desc}` : error
}

// Flush queued analytics (e.g. the bootstrap CancellationSessionStart) before the Auth0
// redirect unloads the page, so the beacon isn't lost. Resolves immediately when the
// analytics instance is a no-op (missing keys / dev bypass).
async function flushBeforeRedirect(): Promise<void> {
	await flushAnalytics()
}

onMounted(async () => {
	// Flush queued analytics before any redirect so events emitted at bootstrap aren't
	// lost when Auth0 navigation unloads the page.
	await flushBeforeRedirect()

	// 1. Came back from Auth0 with code+state. init() already consumed it and set pendingNext.
	const pending = auth.takePendingNext()
	if (pending && auth.isAuthenticated) {
		clearLoopGuard()
		await router.replace(pending)
		return
	}

	// 2. Already signed in (direct visit to /signin with a live session) — bounce out.
	if (auth.isAuthenticated) {
		clearLoopGuard()
		await router.replace(requestedNext.value)
		return
	}

	// 3. Auth0 returned an error in the URL (e.g. callback URL mismatch). Surface it,
	//    strip the query so a reload doesn't replay the same error, and stop here.
	const urlError = readUrlError()
	if (urlError) {
		auth.initError = urlError
		window.history.replaceState({}, document.title, route.path)
		return
	}

	// 4. init() reported a callback exchange error. Show it, let the user click Try again.
	if (auth.initError) return

	// 5. Loop guard: too many auto-attempts in a short window → require a manual click.
	const { allowed } = recordAutoAttempt()
	if (!allowed) {
		auth.initError = `Sign-in loop detected (${MAX_AUTO_ATTEMPTS}+ attempts in ${LOOP_WINDOW_MS / 1000}s). Click “Try again” to continue.`
		return
	}

	// 6. Auto-trigger the Auth0 redirect.
	await initiateSignIn()
})

async function initiateSignIn() {
	auth.initError = null
	try {
		await auth.signIn({ next: requestedNext.value })
		// Under real Auth0 the call above navigates away and never returns. Under devBypass
		// it resolves without doing anything, so we route to the destination ourselves.
		if (devBypassEnabled) {
			auth.status = 'authenticated'
			clearLoopGuard()
			await router.replace(requestedNext.value)
		}
	} catch (err) {
		auth.initError = err instanceof Error ? err.message : String(err)
		// Auth0 SDK exception (redirect kickoff failed) — not an `authedJson` network call, so
		// report it directly here. Mirrors the auth-store init() capture (flow: auth_signin).
		Sentry.captureException(err, { tags: { flow: 'auth_signin', step: 'auth0_login' } })
	}
}

async function manualRetry() {
	clearLoopGuard()
	await initiateSignIn()
}
</script>

<style scoped>
/*
  /signin uses the DS LayoutScreen recipe; the only local CSS left is the
  spinner that fills the slot while the page waits to redirect to Auth0.
  Vue scopes the keyframe name automatically, so `signin-spin` is unique.
*/
.signin__spinner {
	width: var(--size-48);
	height: var(--size-48);
	border: 4px solid transparent;
	border-top-color: var(--background-brand-high);
	border-radius: 50%;
	animation: signin-spin 0.8s linear infinite;
}

@keyframes signin-spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
